import { Effects } from "./effects.js";
import { MessageBus } from "./message_bus.js";
import { Retry } from "./retry.js";
import { Utils } from "./utils.js";
import { APP } from "./app.js";

// Uploads the video parts in order, one at a time. A failed part stays at the
// head of the queue and is retried with backoff for as long as the tab is
// open, because a recording cannot be made again (B1, B3). Nothing here opens
// a modal: the progress block at the bottom of the page tells the tester what
// is going on (B2, B7).
export class Uploader {
  static STALL_MS = 30_000;
  static REQUEST_LIMIT_MS = 10 * 60_000;
  static OFFLINE_RECHECK_MS = 60_000;
  static TROUBLE_AFTER_FAILURES = 5;
  static SLOW_QUEUE_LENGTH = 5;
  static DONE_VISIBLE_MS = 4_000;

  #progressBlock
  #statusElement
  #retryNowButton
  #progressElement
  #progressBarElement
  #summaryElement
  #noticeElement

  #queue
  #nextSeq
  #dropped
  #state
  #isDraining
  #isOffline
  #isVisible
  #progressPercentage
  #consecutiveFailures
  #retryAt
  #wakeUp
  #retryRequested
  #countdownIntervalID
  #doneTimeoutID
  #slowWarningShown
  #isSlowWarningVisible
  #drainedResolvers

  constructor() {
    this.#queue = [];
    this.#nextSeq = 1;
    this.#dropped = [];
    this.#state = "idle";
    this.#isDraining = false;
    this.#isOffline = navigator.onLine === false;
    this.#isVisible = false;
    this.#progressPercentage = 0;
    this.#consecutiveFailures = 0;
    this.#retryAt = null;
    this.#wakeUp = null;
    this.#retryRequested = false;
    this.#slowWarningShown = false;
    this.#isSlowWarningVisible = false;
    this.#drainedResolvers = [];

    this.#progressBlock = document.querySelector("#uploading-progress-block");
    this.#statusElement = document.querySelector("#upload-status");
    this.#retryNowButton = document.querySelector("#upload-retry-now-button");
    this.#progressElement = this.#progressBlock.querySelector(".progress");
    this.#progressBarElement = this.#progressBlock.querySelector(".progress-bar");
    this.#summaryElement = document.querySelector("#upload-queue-summary");
    this.#noticeElement = document.querySelector("#upload-notice");

    this.#setEvents();
    this.#render();
  }

  // API :: INI
  enqueue(blob, date = Date.now()) {
    this.#queue.push(new Uploader.Part(this.#nextSeq++, date, blob));
    console.log("Uploader.enqueue().queue.length: ", this.#queue.length);

    this.#checkQueueGrowth();
    this.#render();
    this.#drain();
  }

  // Read by the beforeunload guard: true while a part is uploading, waiting to
  // retry or waiting for the connection
  isUploadingActive() {
    return this.#queue.length > 0;
  }

  // Resolves once the queue is empty and nothing is uploading or waiting to
  // retry, also when the last part was dropped (B6).
  drained() {
    return new Promise((resolve) => {
      this.#drainedResolvers.push(resolve);
      this.#resolveDrainedIfIdle();
    });
  }

  // Cuts the current wait short. During an upload, the next failure retries
  // at once instead of waiting (B5).
  retryNow() {
    if (this.#wakeUp) {
      this.#wakeUp();
    } else if (this.#state === "uploading") {
      this.#retryRequested = true;
    }
  }
  // API :: END

  #setEvents() {
    window.addEventListener("online", () => {
      this.#isOffline = false;
      this.#render();
      this.retryNow();
    });

    window.addEventListener("offline", () => {
      this.#isOffline = true;
      this.#render();
    });

    window.addEventListener("focus", () => {
      this.retryNow();
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.retryNow();
      }
    });

    this.#retryNowButton.addEventListener("click", () => {
      this.retryNow();
    });
  }

  // The single worker: always the part at the head of the queue, which leaves
  // the queue only after it uploaded or failed for good (B3)
  async #drain() {
    if (this.#isDraining) {
      return;
    }

    this.#isDraining = true;
    clearTimeout(this.#doneTimeoutID);

    try {
      while (this.#queue.length > 0) {
        if (this.#isOffline) {
          // Wait for the "online" event, then try anyway in case the browser
          // got its connection state wrong
          this.#setState("offline");
          await this.#wait(Uploader.OFFLINE_RECHECK_MS);
        }

        const part = this.#queue[0];

        try {
          await this.#upload(part);
          this.#queue.shift();
          this.#consecutiveFailures = 0;
          this.#retryRequested = false;
          this.#isOffline = false;
        } catch (error) {
          await this.#uploadFailed(part, error);
        }
      }
    } finally {
      this.#isDraining = false;
      this.#retryRequested = false;
      this.#isSlowWarningVisible = false;

      if (this.#queue.length === 0) {
        this.#showDone();
      }

      this.#resolveDrainedIfIdle();
    }
  }

  // Aborted when no progress was made for 30 s (B4), and after 10 minutes in
  // any case. An aborted upload has no response, so it is retried.
  async #upload(part) {
    this.#progressPercentage = 0;
    this.#setState("uploading");
    MessageBus.debug(`Uploader: uploading part ${part.seq} (attempt ${part.attempts + 1})`);

    const controller = new AbortController();
    let stallTimeoutID = null;

    const watchStall = () => {
      clearTimeout(stallTimeoutID);
      stallTimeoutID = setTimeout(() => {
        MessageBus.debug(`Uploader: part ${part.seq} made no progress for ${Uploader.STALL_MS / 1000} s, aborting`);
        controller.abort();
      }, Uploader.STALL_MS);
    };

    const limitTimeoutID = setTimeout(() => {
      MessageBus.debug(`Uploader: part ${part.seq} took more than ${Uploader.REQUEST_LIMIT_MS / 60_000} minutes, aborting`);
      controller.abort();
    }, Uploader.REQUEST_LIMIT_MS);

    watchStall();

    try {
      await APP.playcocolaAPI.uploadVideoPart(part.seq, part.date, part.blob, {
        signal: controller.signal,
        onProgress: (progress) => {
          watchStall();
          this.#setProgress(progress);
        }
      });

      MessageBus.debug(`Uploader: part ${part.seq} uploaded`);
    } finally {
      clearTimeout(stallTimeoutID);
      clearTimeout(limitTimeoutID);
    }
  }

  async #uploadFailed(part, error) {
    part.attempts += 1;

    const status = error?.response?.status ?? "no response";
    const details = Utils.normalizeError(error);

    // A permanent failure (B10): drop the part and carry on with the next one
    if (!Retry.isRetriable(error)) {
      this.#queue.shift();
      this.#dropped.push(part.seq);
      MessageBus.debug(`Uploader: part ${part.seq} dropped after a permanent failure (${status}: ${details.message})`);
      return;
    }

    this.#consecutiveFailures += 1;
    const delay = Retry.backoffDelay(part.attempts - 1);
    MessageBus.debug(`Uploader: part ${part.seq} failed (attempt ${part.attempts}, ${status}: ${details.message}), retrying in ${delay} ms`);

    if (this.#retryRequested) {
      this.#retryRequested = false;
      return;
    }

    this.#retryAt = Date.now() + delay;
    this.#setState("waiting");
    this.#countdownIntervalID = setInterval(() => { this.#render(); }, 1000);

    try {
      await this.#wait(delay);
    } finally {
      clearInterval(this.#countdownIntervalID);
      this.#retryAt = null;
    }
  }

  // Resolves after `milliseconds`, or earlier through retryNow()
  #wait(milliseconds) {
    return new Promise((resolve) => {
      const timeoutID = setTimeout(() => { done(); }, milliseconds);

      const done = () => {
        clearTimeout(timeoutID);
        this.#wakeUp = null;
        resolve();
      };

      this.#wakeUp = done;
    });
  }

  #resolveDrainedIfIdle() {
    if (this.#isDraining || this.#queue.length > 0) {
      return;
    }

    this.#drainedResolvers.splice(0).forEach((resolve) => resolve());
  }

  // B8: the recording produces parts faster than they upload
  #checkQueueGrowth() {
    if (this.#slowWarningShown || this.#queue.length < Uploader.SLOW_QUEUE_LENGTH || !APP.recordingManager?.isRecording) {
      return;
    }

    this.#slowWarningShown = true;
    this.#isSlowWarningVisible = true;
    MessageBus.debug(`Uploader: ${this.#queue.length} parts waiting, the connection is slower than the recording`);
  }

  #showDone() {
    this.#setState("done");

    clearTimeout(this.#doneTimeoutID);
    this.#doneTimeoutID = setTimeout(() => {
      if (this.#state === "done") {
        this.#setState("idle");
      }
    }, Uploader.DONE_VISIBLE_MS);
  }

  #setState(state) {
    this.#state = state;
    this.#render();
  }

  // Renders only when the whole percentage changes
  #setProgress(progress) {
    const percentage = Math.round((progress ?? 0) * 100);

    if (percentage !== this.#progressPercentage) {
      this.#progressPercentage = percentage;
      this.#render();
    }
  }

  // Render :: INI
  #render() {
    const parts = this.#queue.length;

    this.#setVisible(parts > 0 || this.#isOffline || this.#state === "done");
    this.#progressBlock.dataset.state = this.#isOffline ? "offline" : this.#state;

    this.#statusElement.textContent = this.#statusText();
    this.#retryNowButton.classList.toggle("d-none", this.#state !== "waiting");

    this.#progressElement.classList.toggle("d-none", this.#state !== "uploading");
    this.#progressElement.setAttribute("aria-valuenow", this.#progressPercentage);
    this.#progressBarElement.style.width = this.#progressPercentage + "%";

    this.#summaryElement.classList.toggle("d-none", parts === 0);
    this.#summaryElement.textContent = this.#summaryText();

    const notice = this.#noticeText();
    this.#noticeElement.classList.toggle("d-none", notice === "");
    this.#noticeElement.textContent = notice;
  }

  #setVisible(visible) {
    if (visible === this.#isVisible) {
      return;
    }

    this.#isVisible = visible;

    if (visible) {
      Effects.delayedFadeIn(this.#progressBlock);
    } else {
      Effects.delayedFadeOut(this.#progressBlock);
    }
  }

  #statusText() {
    if (this.#isOffline) {
      return "You are offline. Uploads will resume automatically when the connection is back.";
    }

    switch (this.#state) {
      case "uploading":
        return `Uploading part ${this.#queue[0]?.seq} (${this.#progressPercentage}%)`;
      case "waiting":
        return `Waiting to retry in ${Math.max(0, Math.ceil((this.#retryAt - Date.now()) / 1000))} s`;
      case "done":
        if (this.#dropped.length > 0) {
          return `Uploads finished, but ${this.#dropped.length} part(s) could not be uploaded`;
        }
        return "All uploads done ✓";
    }

    return "";
  }

  #summaryText() {
    const parts = this.#queue.length;
    const bytes = this.#queue.reduce((sum, part) => sum + part.blob.size, 0);
    const megabytes = (bytes / 1_000_000).toFixed(1);

    return `${parts} ${parts === 1 ? "part" : "parts"} · ${megabytes} MB left to upload. Please don't close this window.`;
  }

  #noticeText() {
    if (this.#consecutiveFailures >= Uploader.TROUBLE_AFTER_FAILURES) {
      return "Having trouble uploading. We keep trying automatically, please don't close this page.";
    }

    if (this.#isSlowWarningVisible) {
      return "Your connection is slower than the recording; uploads will continue after you finish — please keep this tab open.";
    }

    return "";
  }
  // Render :: END

  static Part = class Part {
    seq
    date
    blob
    attempts

    constructor(seq, date, blob) {
      this.seq = seq;
      this.date = date;
      this.blob = blob;
      this.attempts = 0;
    }
  }
}
